/**
 * Trusted Types (CSP `require-trusted-types-for 'script'; trusted-types viko`).
 *
 * The app never writes HTML: markdown renders to React elements (@viko/transcript-ui) and
 * there is no `dangerouslySetInnerHTML`. The only sink we use is the service-worker script
 * URL, so the `viko` policy allows exactly same-origin `/sw.js` and nothing else. No `default`
 * policy exists, so any stray `innerHTML =` string throws instead of being silently allowed.
 */
type Policy = { createScriptURL(url: string): unknown }
type TT = { createPolicy(name: string, rules: { createScriptURL?: (u: string) => string }): Policy }

const ALLOWED_SCRIPT_URLS = new Set(['/sw.js', '/dev-sw.js?dev-sw'])

let policy: Policy | null | undefined

export function trustedPolicy(): Policy | null {
  if (policy !== undefined) return policy
  const tt = (globalThis as { trustedTypes?: TT }).trustedTypes
  policy = tt
    ? tt.createPolicy('viko', {
        createScriptURL(u: string) {
          const url = new URL(u, location.origin)
          if (url.origin !== location.origin || !ALLOWED_SCRIPT_URLS.has(url.pathname + url.search))
            throw new TypeError(`Blocked script URL: ${u}`)
          return url.pathname + url.search
        },
      })
    : null
  return policy
}

/** A script URL accepted by Trusted-Types-enforcing browsers (plain string elsewhere). */
export function scriptURL(u: string): string {
  const p = trustedPolicy()
  return (p ? p.createScriptURL(u) : u) as string
}
